import { useMemo } from 'react'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'

import { useDebouncedValue } from './useDebouncedValue'

const UNICODE_WHITESPACE_RUN_PATTERN = /\s+/gu

export const SEARCH_DEBOUNCE_MS = 250
export const SEARCH_MIN_QUERY_LENGTH = 2
export const SEARCH_MAX_QUERY_LENGTH = 200

const EMPTY_RESULTS = []
const EMPTY_WARNINGS = []
const EMPTY_PAGES = []

export function normalizeSearchQuery(value, maxLength = SEARCH_MAX_QUERY_LENGTH) {
  const normalized = String(value || '').trim().replace(UNICODE_WHITESPACE_RUN_PATTERN, ' ')
  return Array.from(normalized).slice(0, maxLength).join('')
}

export function canSearchQuery(value, minLength = SEARCH_MIN_QUERY_LENGTH) {
  return Array.from(normalizeSearchQuery(value)).length >= minLength
}

// Search endpoints answer { error: { code, message, retryable } }; a product
// gate answers { error: "..." }.
export function searchError(error) {
  const apiError = error?.response?.data?.error
  if (typeof apiError === 'string' && apiError) {
    return { code: 'search_failed', message: apiError, retryable: true }
  }
  if (apiError && typeof apiError === 'object') {
    return {
      code: apiError.code || 'search_failed',
      message: apiError.message || '',
      retryable: Boolean(apiError.retryable),
    }
  }
  return { code: 'search_failed', message: error?.message || '', retryable: true }
}

// Pages can overlap as the index moves; the first page to return an item keeps it.
function flattenResults(pages, getItemKey) {
  const seen = new Set()
  return pages.flatMap((page) => (page.results || []).filter((item) => {
    const key = getItemKey(item)
    if (key === undefined || key === null || key === '') return true
    const identity = String(key)
    if (seen.has(identity)) return false
    seen.add(identity)
    return true
  }))
}

function flattenWarnings(pages) {
  return [...new Set(pages.flatMap((page) => page.warnings || EMPTY_WARNINGS))]
}

/**
 * A debounced, cursor-paged search. `queryKey` scopes the cache to the caller
 * and must name everything `search` depends on besides the query; the
 * normalized query is appended to it. `search({ query, cursor, signal })`
 * resolves to a page of `{ results, warnings, next_cursor }`. Queries shorter
 * than `minLength` are not sent and longer than `maxLength` are cut.
 */
export function useSearchQuery({
  queryKey,
  query,
  search,
  getItemKey,
  enabled = true,
  debounceMs = SEARCH_DEBOUNCE_MS,
  minLength = SEARCH_MIN_QUERY_LENGTH,
  maxLength = SEARCH_MAX_QUERY_LENGTH,
}) {
  const normalizedQuery = useMemo(() => normalizeSearchQuery(query, maxLength), [query, maxLength])
  const debouncedQuery = useDebouncedValue(normalizedQuery, Math.max(0, debounceMs))
  const canSearch = Boolean(enabled && canSearchQuery(normalizedQuery, minLength))
  const isDebouncing = canSearch && debouncedQuery !== normalizedQuery

  const searchQuery = useInfiniteQuery({
    queryKey: [...queryKey, normalizedQuery],
    queryFn: ({ pageParam, signal }) => search({ query: normalizedQuery, cursor: pageParam ?? undefined, signal }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    enabled: canSearch && !isDebouncing,
    placeholderData: keepPreviousData,
    retry: false,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })

  // Another query's results are never shown while this one loads.
  const hasStalePlaceholder = searchQuery.isPlaceholderData && Boolean(searchQuery.data?.pages?.length)
  const showingCurrentResults = !isDebouncing && !hasStalePlaceholder && (!searchQuery.isError || searchQuery.isFetchNextPageError)
  const pages = showingCurrentResults ? (searchQuery.data?.pages || EMPTY_PAGES) : EMPTY_PAGES
  const results = useMemo(() => flattenResults(pages, getItemKey), [pages, getItemKey])
  const warnings = useMemo(() => flattenWarnings(pages), [pages])
  const canLoadMore = canSearch
    && showingCurrentResults
    && !searchQuery.isFetching
    && Boolean(searchQuery.hasNextPage)
  const loadMore = () => (canLoadMore ? searchQuery.fetchNextPage() : Promise.resolve())

  if (!canSearch) {
    return {
      status: 'idle',
      query: normalizedQuery,
      pages: EMPTY_PAGES,
      results: EMPTY_RESULTS,
      warnings: EMPTY_WARNINGS,
      hasMore: false,
      isLoadingMore: false,
      loadMore,
      error: null,
    }
  }

  return {
    status: searchQuery.isError ? 'error' : (isDebouncing || hasStalePlaceholder || searchQuery.isPending || searchQuery.isFetching ? 'loading' : 'success'),
    query: normalizedQuery,
    pages,
    results,
    warnings,
    hasMore: canLoadMore,
    isLoadingMore: searchQuery.isFetchingNextPage,
    loadMore,
    error: searchQuery.isError ? searchError(searchQuery.error) : null,
  }
}
